import React from 'react';
import { useEffect, useCallback, useRef } from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import { toast } from 'react-toastify';
import ImageView from '../../../components/ImageView';
import { LotData } from './AuctionDetailPopup';
import {
  LeftSection,
  LotsTitle,
  ProgressContainer,
  ProgressLabel,
  ProgressBar,
  ProgressFill,
  LotsContainer,
  LotCard,
  LotCardImage,
  LotCardContent,
  LotCardHeader,
  LotCardChip,
  LotCardStatus,
  LotCardName,
  LotCardPrice,
  LotCardTime
} from './AuctionDetailStyles';

interface AuctionLotsListProps {
  lots: LotData[];
  selectedLotId: string;
  auctionStartAt?: string; // 경매 시작 시간 추가
  auctionEndAt?: string; // 경매 종료 시간 추가
  currentLot?: { // 소켓에서 받은 현재 진행 중인 lot 정보
    lotIndex: number;
    lotNo: number;
    lotName?: string;
  } | null;
  isAuctionEnded?: boolean; // 소켓에서 받은 경매 종료 상태
  onLotSelect: (lotId: string) => void;
  onAuctionEnded?: () => void; // 경매 종료 시 콜백 (입찰내역 업데이트용)
}

const AuctionLotsList: React.FC<AuctionLotsListProps> = ({
  lots,
  selectedLotId,
  auctionStartAt,
  auctionEndAt,
  currentLot, // 소켓에서 받은 현재 진행 중인 lot
  isAuctionEnded, // 소켓에서 받은 경매 종료 상태
  onLotSelect,
  onAuctionEnded // 경매 종료 시 콜백
}) => {
  // 이전 소켓 lot 추적을 위한 ref (lot 변경 감지용)
  const prevSocketLotRef = useRef<{ lotIndex: number; lotNo: number } | null>(null);
  
  // 개발 모드에서 상태 확인용 로그
  const isDebugMode = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
  
  // selectedLotId 변화 추적 (디버깅용)
  useEffect(() => {
    if (isDebugMode) {
      devLog('🎯 [AuctionLotsList] selectedLotId 변경:', {
        selectedLotId,
        totalLots: lots.length,
        lotIds: lots.map(lot => lot.lotId)
      });
    }
  }, [selectedLotId, lots, isDebugMode]);
  
  // 라이브 경매가 시작되기 전인지 확인
  const isBeforeAuctionStart = auctionStartAt ? new Date() < new Date(auctionStartAt) : false;
  
  // 경매가 종료되었는지 확인 (소켓 이벤트 우선)
  const isAfterAuctionEnd = isAuctionEnded || (auctionEndAt ? new Date() > new Date(auctionEndAt) : false);
  
  if (isDebugMode) {
    devLog('📋 [AuctionLotsList] 상태 확인:', {
      auctionStartAt,
      auctionEndAt,
      isBeforeAuctionStart,
      isAfterAuctionEnd,
      isAuctionEnded, // 소켓 이벤트 상태
      currentTime: new Date().toISOString(),
      totalLots: lots.length,
      currentLot: currentLot ? {
        lotIndex: currentLot.lotIndex,
        lotNo: currentLot.lotNo,
        lotName: currentLot.lotName
      } : null,
      lotStatuses: lots.map(lot => ({
        lotId: lot.lotId,
        originalStatus: lot.status
      }))
    });
  }
  
  // 현재 진행 중인 lot을 찾는 함수 (소켓 이벤트 우선)
  const getCurrentLot = useCallback(() => {
    // 1. 소켓 이벤트로 받은 currentLot이 있으면 우선 사용
    if (currentLot && currentLot.lotIndex) {
      // lots 배열에서 해당 lot 찾기 (index나 lotNo로 매칭)
      const socketLot = lots.find(lot => {
        // lot.lotId를 파싱해서 lotNo 추출 (LOT001 -> 1)
        const lotNoFromId = lot.lotId.replace(/^LOT0*/, '');
        const lotNoNumber = parseInt(lotNoFromId);
        return lotNoNumber === currentLot.lotNo;
      });
      
      if (socketLot) {
        if (isDebugMode) {
          devLog('🎯 [AuctionLotsList] 소켓 이벤트로 받은 현재 lot 사용:', {
            lotIndex: currentLot.lotIndex,
            lotNo: currentLot.lotNo,
            lotName: currentLot.lotName,
            foundLotId: socketLot.lotId
          });
        }
        return socketLot;
      }
    }
    
    // 2. 소켓 이벤트 정보가 없으면 기존 로직 사용 (fallback)
    if (!lots || lots.length === 0) return null;
    
    // 현재 진행 중인 상태 (ONGOING)인 lot 찾기
    const ongoingLot = lots.find(lot => lot.status === 'ONGOING');
    if (ongoingLot) {
      if (isDebugMode) {
        devLog('🎯 [AuctionLotsList] fallback으로 ONGOING 상태 lot 사용:', {
          lotId: ongoingLot.lotId,
          reason: '소켓 이벤트 정보 없음'
        });
      }
      return ongoingLot;
    }
    
    // 진행 중인 lot이 없으면 첫 번째 WAITING 상태 lot
    const waitingLot = lots.find(lot => lot.status === 'WAITING');
    if (waitingLot && isDebugMode) {
      devLog('🎯 [AuctionLotsList] fallback으로 WAITING 상태 lot 사용:', {
        lotId: waitingLot.lotId,
        reason: 'ONGOING lot 없음'
      });
    }
    
    return waitingLot || null;
  }, [currentLot, lots, isDebugMode]);

  // 소켓 이벤트에 따른 자동 lot 선택 (소켓 lot 변경시에만)
  useEffect(() => {
    // 소켓에서 받은 currentLot 정보가 있을 때
    if (currentLot && currentLot.lotIndex && currentLot.lotNo && lots && lots.length > 0) {
      const currentSocketLot = { lotIndex: currentLot.lotIndex, lotNo: currentLot.lotNo };
      
      // 이전 소켓 lot와 비교하여 실제로 변경되었는지 확인
      const isSocketLotChanged = !prevSocketLotRef.current || 
                                prevSocketLotRef.current.lotIndex !== currentSocketLot.lotIndex ||
                                prevSocketLotRef.current.lotNo !== currentSocketLot.lotNo;
      
      if (isSocketLotChanged) {
        if (isDebugMode) {
          devLog('🔄 [AuctionLotsList] 소켓 lot 변경 감지:', {
            prev: prevSocketLotRef.current,
            current: currentSocketLot,
            reason: '소켓에서 새로운 lot 진행 시작'
          });
        }
        
        // 이전 소켓 lot 정보 업데이트
        prevSocketLotRef.current = currentSocketLot;
        
        // 소켓 정보를 기반으로 해당 lot 직접 찾기
        const targetLot = lots.find(lot => {
          // lot.lotId를 파싱해서 lotNo 추출 (LOT001 -> 1)
          const lotNoFromId = lot.lotId.replace(/^LOT0*/, '');
          const lotNoNumber = parseInt(lotNoFromId);
          return lotNoNumber === currentLot.lotNo;
        });
        
        if (targetLot) {
          if (isDebugMode) {
            devLog('🔄 [AuctionLotsList] 소켓 변경으로 인한 자동 선택:', {
              targetLotId: targetLot.lotId,
              currentSelectedLotId: selectedLotId
            });
          }
          
          // 소켓 lot가 변경되었으므로 자동으로 해당 lot 선택
          onLotSelect(targetLot.lotId);
        }
      }
    }
    // 필요한 의존성 추가: lots 배열과 onLotSelect
  }, [currentLot?.lotIndex, currentLot?.lotNo, lots, onLotSelect, isDebugMode]);

  // 경매 종료 감지 및 토스트 표시
  const hasShownAuctionEndToast = useRef(false);
  
  useEffect(() => {
    // 경매가 종료되고 아직 토스트를 표시하지 않았다면
    if (isAuctionEnded && !hasShownAuctionEndToast.current) {
      hasShownAuctionEndToast.current = true;
      
      if (isDebugMode) {
        devLog('🏁 [AuctionLotsList] 경매 종료 감지 - 토스트 표시');
      }
      
      // 토스트 표시
      toast.success('🏁 모든 LOT 경매가 종료되었습니다', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // 입찰내역 테이블 업데이트 콜백 실행
      if (onAuctionEnded) {
        setTimeout(() => {
          onAuctionEnded();
        }, 500); // 토스트 표시 후 약간의 딜레이
      }
    }
    
    // 경매가 다시 시작되면 토스트 플래그 리셋
    if (!isAuctionEnded && hasShownAuctionEndToast.current) {
      hasShownAuctionEndToast.current = false;
      if (isDebugMode) {
        devLog('🔄 [AuctionLotsList] 경매 재시작 - 토스트 플래그 리셋');
      }
    }
  }, [isAuctionEnded, onAuctionEnded, isDebugMode]);
  // 이미지 URL 처리 함수 (LiveSection과 동일)
  const getProfileImageSrc = (profileImageUrl?: string | null) => {
    if (!profileImageUrl || profileImageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
      return profileImageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (profileImageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
  };

  // LOT의 첫 번째 이미지를 가져오는 함수
  const getFirstImageUrl = (lot: LotData): string => {
    // images 배열이 있는 경우 (소켓 데이터)
    if ((lot as any).images && Array.isArray((lot as any).images) && (lot as any).images.length > 0) {
      return getProfileImageSrc((lot as any).images[0]);
    }
    // thumbnail이 있는 경우 (기존 데이터)
    else if (lot.thumbnail && lot.thumbnail.trim() !== "") {
      return getProfileImageSrc(lot.thumbnail);
    }
    // 기본 이미지
    return "/common/nodata.svg";
  };
  // 가격 포맷팅 함수
  const formatPrice = (price: number, currency: 'KRW' | 'USD') => {
    if (currency === 'KRW') {
      return `₩${price.toLocaleString()}`;
    } else {
      return `$${price.toLocaleString()}`;
    }
  };

  const getStatusText = (lot: LotData) => {
    // 경매가 종료된 후라면 모든 상태를 '경매종료'로 표시
    if (isAfterAuctionEnd) {
      return '경매종료';
    }
    
    // 라이브 경매 시작 전이면 모든 상태를 '진행예정'으로 표시
    if (isBeforeAuctionStart) {
      return '진행예정';
    }
    
    // 소켓 이벤트 정보를 기반으로 동적 상태 결정
    const currentOngoingLot = getCurrentLot();
    
    if (currentOngoingLot && currentOngoingLot.lotId === lot.lotId) {
      return '진행 중';
    }
    
    // 원본 상태 기반 판단
    switch (lot.status) {
      case 'ONGOING': return '진행 중';
      case 'WAITING': return '대기';
      case 'COMPLETED': return '경매종료';
      default: return '대기';
    }
  };

  // 동적으로 lot 상태를 계산하는 함수
  const getDynamicLotStatus = (lot: LotData): 'WAITING' | 'ONGOING' | 'COMPLETED' | 'SCHEDULED' => {
    // 경매가 종료된 후라면 모든 상태를 COMPLETED로
    if (isAfterAuctionEnd) {
      return 'COMPLETED';
    }
    
    // 라이브 경매 시작 전이면 모든 상태를 SCHEDULED로
    if (isBeforeAuctionStart) {
      return 'SCHEDULED';
    }
    
    // 소켓 이벤트 정보를 기반으로 동적 상태 결정
    const currentOngoingLot = getCurrentLot();
    
    // 현재 진행 중인 lot인지 확인
    if (currentOngoingLot && currentOngoingLot.lotId === lot.lotId) {
      return 'ONGOING';
    }
    
    // 원본 상태가 COMPLETED면 그대로 유지 (이전에 완료된 lot들)
    if (lot.status === 'COMPLETED') {
      return 'COMPLETED';
    }
    
    // 현재 진행 중인 lot보다 번호가 작으면서 COMPLETED가 아닌 경우, 완료된 것으로 간주
    if (currentOngoingLot) {
      const currentLotNoFromId = currentOngoingLot.lotId.replace(/^LOT0*/, '');
      const currentLotNumber = parseInt(currentLotNoFromId);
      
      const thisLotNoFromId = lot.lotId.replace(/^LOT0*/, '');
      const thisLotNumber = parseInt(thisLotNoFromId);
      
      // 현재 진행 중인 lot보다 번호가 작으면 완료된 것으로 간주
      if (thisLotNumber < currentLotNumber) {
        return 'COMPLETED';
      }
    }
    
    // 나머지는 원본 상태 또는 WAITING
    return lot.status === 'ONGOING' ? 'WAITING' : lot.status;
  };

  const getPriceInfo = (lot: LotData) => {
    switch (lot.status) {
      case 'COMPLETED':
        return `Final: ${formatPrice(lot.currentPriceKrw, 'KRW')}`;
      case 'ONGOING':
        const bidCount = lot.bidHistory.length;
        return `${formatPrice(lot.currentPriceKrw, 'KRW')} (${bidCount} bids)`;
      case 'WAITING':
        return `Starting: ${formatPrice(lot.startPriceKrw, 'KRW')}`;
      default:
        return `Starting: ${formatPrice(lot.startPriceKrw, 'KRW')}`;
    }
  };

  const getTimeInfo = (lot: LotData) => {
    switch (lot.status) {
      case 'ONGOING':
        return 'Ends in 2m 30s';
      default:
        return '';
    }
  };

  const completedLots = lots.filter(lot => getDynamicLotStatus(lot) === 'COMPLETED').length;
  const totalLots = lots.length;
  
  // 경매 종료 후라면 진행률 100%, 라이브 경매 시작 전이면 진행률 0%, 아니면 정상 계산
  const progressPercentage = isAfterAuctionEnd ? 100 : 
                            isBeforeAuctionStart ? 0 : 
                            (totalLots > 0 ? (completedLots / totalLots) * 100 : 0);

  return (
    <LeftSection>
      <LotsTitle>경매 Lots</LotsTitle>
      
      {/* 프로세스 바 */}
      <ProgressContainer>
        <ProgressLabel>
          {isAfterAuctionEnd 
            ? `경매 종료 - ${totalLots}개 LOT 완료`
            : isBeforeAuctionStart 
              ? `${totalLots}개 LOT 진행예정` 
              : `${totalLots}개 LOT 중 ${totalLots - completedLots}개 남음`}
        </ProgressLabel>
        <ProgressBar>
          <ProgressFill percentage={progressPercentage} />
        </ProgressBar>
      </ProgressContainer>

      {/* LOT 카드들 */}
      <LotsContainer>
        {lots.map((lot) => {
          const isSelected = lot.lotId === selectedLotId;
          
          // 디버그 로깅 - 선택 상태 확인
          if (isDebugMode && (isSelected || lot.lotId === selectedLotId)) {
            devLog('🎯 [AuctionLotsList] LOT 카드 선택 상태:', {
              lotId: lot.lotId,
              selectedLotId,
              isSelected,
              comparison: `"${lot.lotId}" === "${selectedLotId}"`
            });
          }
          
          return (
            <LotCard 
              key={lot.lotId} 
              isSelected={isSelected}
              status={getDynamicLotStatus(lot)}
              onClick={() => {
                if (isDebugMode) {
                  devLog('👤 [AuctionLotsList] LOT 카드 클릭:', {
                    lotId: lot.lotId,
                    currentSelectedLotId: selectedLotId
                  });
                }
                onLotSelect(lot.lotId);
              }}
            >
              <LotCardImage>
                <ImageView
                  imageUrl={getFirstImageUrl(lot)}
                  width={80}
                  height={80}
                  borderRadius={8}
                  alt={lot.lotName}
                  objectFit="cover"
                />
              </LotCardImage>
              <LotCardContent>
                <LotCardHeader>
                  <LotCardChip>{lot.lotId}</LotCardChip>
                  <LotCardStatus status={getDynamicLotStatus(lot)}>
                    {getStatusText(lot)}
                  </LotCardStatus>
                </LotCardHeader>
                <LotCardName>{lot.lotName}</LotCardName>
                <LotCardPrice>{getPriceInfo(lot)}</LotCardPrice>
                {getTimeInfo(lot) && <LotCardTime>{getTimeInfo(lot)}</LotCardTime>}
              </LotCardContent>
            </LotCard>
          );
        })}
      </LotsContainer>
    </LeftSection>
  );
};

export default AuctionLotsList;
